import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History, Loader2, ChevronDown, ChevronUp, CheckCircle2, XCircle, Clock, ShieldAlert, MessageSquare, Bot, Brain } from 'lucide-react';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import { ActionRow, BeforeAfter, RichText, SectionTitle, TraceView, VerdictBadge, useFmt } from './tmShared';
import trafficManagerService, { ChangeItem, TrafficManager, TrafficRun } from '../../services/trafficManagerService';

const selCls = 'px-3 py-2 text-base sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white';

// ---------------------------------------------------------------------------
// Mudanças: tudo que o robô fez ou tentou fazer, com origem e efeito medido
// ---------------------------------------------------------------------------
export const ChangesTab: React.FC<{ manager: TrafficManager; campaignFilter: string; onCampaignFilter: (id: string) => void }> = ({ manager, campaignFilter, onCampaignFilter }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const cur = manager.currency || 'BRL';
  const campaigns = manager.snapshot?.campanhas || [];
  const [status, setStatus] = useState('');
  const [days, setDays] = useState(60);
  const [items, setItems] = useState<ChangeItem[] | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setItems(null);
    try {
      const r = await trafficManagerService.changes(manager.id, { campaign_id: campaignFilter || undefined, status: status || undefined, days });
      setItems(r.data);
    } catch { setItems([]); }
  }, [manager.id, campaignFilter, status, days]);
  useEffect(() => { load(); }, [load]);

  const origem = (c: ChangeItem) => {
    if (c.run_trigger === 'chat') return <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{t('trafficManager.changes.fromChat', 'pedido na conversa')}{c.run_request ? `: "${c.run_request.slice(0, 80)}${c.run_request.length > 80 ? '…' : ''}"` : ''}</span>;
    if (c.run_trigger === 'manual') return <span className="inline-flex items-center gap-1"><Bot className="w-3 h-3" />{t('trafficManager.changes.fromManual', 'rodada manual')}</span>;
    return <span className="inline-flex items-center gap-1"><Bot className="w-3 h-3" />{t('trafficManager.changes.fromCron', 'rodada diária')}</span>;
  };

  // Agrupa por dia
  const grupos: [string, ChangeItem[]][] = [];
  for (const c of items || []) {
    const d = (c.decided_at || c.at || c.run_at).slice(0, 10);
    const g = grupos.find(([k]) => k === d);
    if (g) g[1].push(c); else grupos.push([d, [c]]);
  }

  return (
    <Card>
      <SectionTitle icon={History}>{t('trafficManager.changes.title', 'Todas as mudanças')}</SectionTitle>
      <p className="text-sm text-gray-500 -mt-2 mb-4">{t('trafficManager.changes.help', 'Cada mudança com o motivo, de onde veio, quem aprovou e o efeito medido depois da maturação.')}</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <select className={selCls} value={campaignFilter} onChange={(e) => onCampaignFilter(e.target.value)} aria-label={t('trafficManager.changes.campaign', 'Campanha') as string}>
          <option value="">{t('trafficManager.changes.allCampaigns', 'Todas as campanhas')}</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </select>
        <select className={selCls} value={status} onChange={(e) => setStatus(e.target.value)} aria-label={t('trafficManager.changes.status', 'Situação') as string}>
          <option value="">{t('trafficManager.changes.allStatus', 'Todas as situações')}</option>
          {['executed', 'approved', 'proposed', 'blocked', 'rejected', 'failed', 'expired'].map((s) => <option key={s} value={s}>{t(`trafficManager.status.${s}`)}</option>)}
        </select>
        <select className={selCls} value={days} onChange={(e) => setDays(Number(e.target.value))} aria-label={t('trafficManager.changes.period', 'Período') as string}>
          {[7, 30, 60, 90].map((d) => <option key={d} value={d}>{t('trafficManager.changes.lastDays', 'Últimos {{n}} dias', { n: d })}</option>)}
        </select>
      </div>
      {items === null && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>}
      {items && items.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">{t('trafficManager.changes.empty', 'Nenhuma mudança neste filtro.')}</p>}
      <div className="space-y-6">
        {grupos.map(([dia, lista]) => (
          <div key={dia}>
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{f.day(dia)}</div>
            <div className="divide-y divide-gray-100 dark:divide-gray-700 border-t border-gray-100 dark:border-gray-700">
              {lista.map((c) => (
                <ActionRow key={c.id} a={c} cur={cur} extra={(
                  <div className="mt-1.5 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-gray-500">
                      <span>{f.dateTime(c.decided_at || c.at)}</span>
                      {origem(c)}
                      {c.status === 'approved' && <span>{t('trafficManager.changes.approvedByYou', 'aprovada por você')}</span>}
                      {c.impacto && <VerdictBadge v={c.impacto.veredito} daysLeft={c.impacto.dias_faltando} reason={c.impacto.motivo_veredito} />}
                      {c.impacto && c.impacto.veredito !== 'maturando' && (
                        <button type="button" className="text-primary-600 hover:underline" onClick={() => setOpen((p) => ({ ...p, [c.id]: !p[c.id] }))}>
                          {open[c.id] ? t('trafficManager.changes.hideEffect', 'esconder efeito') : t('trafficManager.changes.showEffect', 'ver efeito')}
                        </button>
                      )}
                    </div>
                    {open[c.id] && c.impacto && <BeforeAfter antes={c.impacto.antes} depois={c.impacto.depois} cur={cur} />}
                  </div>
                )} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Rodadas: relatório + ações + o passo a passo do raciocínio
// ---------------------------------------------------------------------------
export const RunsTab: React.FC<{
  manager: TrafficManager;
  runs: TrafficRun[];
  onDecide: (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => Promise<void>;
}> = ({ manager, runs, onDecide }) => {
  const { t } = useTranslation();
  const f = useFmt();
  const cur = manager.currency || 'BRL';
  const [open, setOpen] = useState<Record<string, boolean>>(() => (runs[0] ? { [runs[0].id]: true } : {}));
  const [full, setFull] = useState<Record<string, TrafficRun | 'loading'>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const routine = runs.filter((r) => r.trigger !== 'chat');

  const loadTrace = async (id: string) => {
    if (full[id]) { setFull((p) => { const n = { ...p }; delete n[id]; return n; }); return; }
    setFull((p) => ({ ...p, [id]: 'loading' }));
    try { const r = await trafficManagerService.getRun(id); setFull((p) => ({ ...p, [id]: r })); } catch { setFull((p) => { const n = { ...p }; delete n[id]; return n; }); }
  };
  const decide = async (runId: string, actionId: string | undefined, d: 'approve' | 'reject') => {
    setBusy(runId);
    try { await onDecide(runId, actionId, d); } finally { setBusy(null); }
  };

  return (
    <Card>
      <SectionTitle icon={Clock}>{t('trafficManager.runs.title', 'Rodadas do robô')}</SectionTitle>
      {routine.length === 0 && <p className="text-sm text-gray-500">{t('trafficManager.noRuns', 'Nenhuma rodada ainda. Clique em "Rodar agora" para a primeira análise.')}</p>}
      <div className="space-y-3">
        {routine.map((run) => {
          const acoes = run.actions || [];
          const pend = acoes.filter((a) => a.status === 'proposed');
          const feitas = acoes.filter((a) => ['executed', 'approved'].includes(a.status)).length;
          const barradas = acoes.filter((a) => a.status === 'blocked').length;
          const icon = run.status === 'running' ? <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
            : run.status === 'failed' ? <XCircle className="w-4 h-4 text-red-500" />
            : run.status === 'skipped' ? <Clock className="w-4 h-4 text-gray-400" />
            : pend.length ? <ShieldAlert className="w-4 h-4 text-amber-500" />
            : <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
          const aberto = !!open[run.id];
          const tr = full[run.id];
          return (
            <div key={run.id} className="border border-gray-200 dark:border-gray-700 rounded-xl">
              <button type="button" onClick={() => setOpen((p) => ({ ...p, [run.id]: !p[run.id] }))} className="w-full flex items-center gap-3 p-4 text-left" aria-expanded={aberto}>
                {icon}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {f.dateTime(run.created_at)} · {run.trigger === 'manual' ? t('trafficManager.runs.manual', 'rodada manual') : t('trafficManager.runs.auto', 'rodada diária')}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {run.status === 'running' ? t('trafficManager.analyzing', 'Analisando as campanhas…')
                      : run.error ? run.error
                      : t('trafficManager.runs.counts', '{{d}} feita(s) · {{p}} aguardando · {{b}} barrada(s) pelas travas', { d: feitas, p: pend.length, b: barradas })}
                  </div>
                </div>
                {aberto ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {aberto && (
                <div className="px-4 pb-4 space-y-3">
                  {run.summary && <RichText text={run.summary} className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/60 rounded-lg p-4" />}
                  {pend.length > 1 && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="secondary" disabled={busy === run.id} onClick={() => decide(run.id, undefined, 'reject')}>{t('trafficManager.rejectAll', 'Recusar todas')}</Button>
                      <Button size="sm" disabled={busy === run.id} isLoading={busy === run.id} onClick={() => decide(run.id, undefined, 'approve')}>{t('trafficManager.approveAll', 'Aprovar todas ({{n}})', { n: pend.length })}</Button>
                    </div>
                  )}
                  {acoes.length > 0 && (
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {acoes.map((a) => <ActionRow key={a.id} a={a} cur={cur} busy={busy === run.id} onApprove={() => decide(run.id, a.id, 'approve')} onReject={() => decide(run.id, a.id, 'reject')} />)}
                    </div>
                  )}
                  {run.status !== 'running' && (
                    <button type="button" onClick={() => loadTrace(run.id)} className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
                      <Brain className="w-3.5 h-3.5" />{tr ? t('trafficManager.chat.hideReasoning', 'Esconder raciocínio') : t('trafficManager.runs.showTrace', 'Ver o passo a passo do raciocínio')}
                    </button>
                  )}
                  {tr === 'loading' && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                  {tr && tr !== 'loading' && (
                    <div className="pt-1">
                      <TraceView steps={tr.trace || []} />
                      <p className="mt-3 text-[11px] text-gray-400">{t('trafficManager.runs.tokens', '{{n}} tokens de IA nesta rodada', { n: f.int(tr.tokens_used || 0) })}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
};
